import Cookies from 'js-cookie';
import { ENV } from '@/constant';

export const COOKIE_NAMES = {
    AUTH_TOKEN: 'auth_token',
    ACCOUNT_VERIFICATION_ID: 'account_verification_id',
    USER_LOCATION: 'user_location',
} as const;

// Determine if cookies should be secure (HTTPS only)
// Only mark cookies as `secure` when the current page is served over HTTPS.
// Setting `secure` on HTTP origins (like local IPs) prevents cookies from being saved.
const shouldUseSecureCookies = () => {
    if (typeof window !== 'undefined') {
        return window.location.protocol === 'https:';
    }
    // Fallback for non-browser contexts: assume production wants secure
    return !!ENV.IS_PRODUCTION;
};

export const cookieUtils = {
    setAuthToken: (token: string, options?: Cookies.CookieAttributes) => {
        const isSecure = shouldUseSecureCookies();

        const defaultOptions: Cookies.CookieAttributes = {
            expires: 7,
            secure: isSecure,
            sameSite: 'strict',
            ...options
        };
        Cookies.set(COOKIE_NAMES.AUTH_TOKEN, token, defaultOptions);
    },

    getAuthToken: (): string | undefined => {

        return Cookies.get(COOKIE_NAMES.AUTH_TOKEN);
    },

    removeAuthToken: () => {
        Cookies.remove(COOKIE_NAMES.AUTH_TOKEN);
    },

    setAccountVerificationId: (id: string, options?: Cookies.CookieAttributes) => {
        const isSecure = shouldUseSecureCookies();
        const defaultOptions: Cookies.CookieAttributes = {
            expires: 1,
            secure: isSecure,
            sameSite: 'strict',
            ...options
        };
        Cookies.set(COOKIE_NAMES.ACCOUNT_VERIFICATION_ID, id, defaultOptions);
    },

    getAccountVerificationId: (): string | undefined => {
        return Cookies.get(COOKIE_NAMES.ACCOUNT_VERIFICATION_ID);
    },

    removeAccountVerificationId: () => {
        Cookies.remove(COOKIE_NAMES.ACCOUNT_VERIFICATION_ID);
    },

    clearAllAuthCookies: () => {
        Cookies.remove(COOKIE_NAMES.AUTH_TOKEN);
        Cookies.remove(COOKIE_NAMES.ACCOUNT_VERIFICATION_ID);
    },

    isAuthenticated: (): boolean => {
        return !!cookieUtils.getAuthToken();
    },

    setAuthenticationData: (token: string, accountVerificationId?: string) => {
        cookieUtils.setAuthToken(token);
        if (accountVerificationId) {
            cookieUtils.setAccountVerificationId(accountVerificationId);
        }
    },

    setUserLocation: (latitude: string | number, longitude: string | number, options?: Cookies.CookieAttributes) => {
        const isSecure = shouldUseSecureCookies();
        const defaultOptions: Cookies.CookieAttributes = {
            expires: 7,
            secure: isSecure,
            sameSite: 'strict',
            ...options
        };
        const locationData = JSON.stringify({ latitude: parseFloat(String(latitude)), longitude: parseFloat(String(longitude)) });
        Cookies.set(COOKIE_NAMES.USER_LOCATION, locationData, defaultOptions);
    },

    getUserLocation: (): { latitude: number; longitude: number } | null => {
        const locationData = Cookies.get(COOKIE_NAMES.USER_LOCATION);
        if (!locationData) return null;
        try {
            return JSON.parse(locationData);
        } catch (error) {
            console.error('Error parsing location data:', error);
            return null;
        }
    },

    removeUserLocation: () => {
        Cookies.remove(COOKIE_NAMES.USER_LOCATION);
    },
};